/**
 * DashboardLayout Component
 *
 * shadcn/ui SidebarProvider + SidebarInset構成によるメインレイアウト
 * AuthenticatedLayoutからの移行（Material-UI → shadcn/ui）
 *
 * Features:
 * - SidebarProvider: デフォルト開状態でSidebar制御
 * - SidebarInset: コンテンツエリア最適化
 * - 動的パンくずリスト: URLベースで階層表示 (desktop のみ)
 * - レスポンシブ対応: Mobile/Tablet/Desktop
 *   - desktop (md 以上): AppSidebar + breadcrumb header (既存維持)
 *   - mobile (md 未満): MobileHeader + BottomNav + MobileDrawer (Phase 1p)
 * - GlobalSearchCommand統合 (Cmd+K)
 *
 * Phase 1p (#3028): Mobile Nav + A11y SSOT 統合
 * Design System v3.0準拠
 */

import React, { Suspense, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';

import { RouteErrorBoundary } from '@/components/ErrorBoundary';

import { AppSidebar } from './AppSidebar';
import { BottomNav } from './BottomNav';
import { GlobalSearchCommand } from './GlobalSearchCommand';
import { MobileDrawer } from './MobileDrawer';
import { MobileHeader } from './MobileHeader';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { Separator } from '@/components/ui/separator';
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from '@/components/ui/sidebar';

// パンくずリスト用のパス情報
const pathTitles: Record<string, string> = {
  '/dashboard': 'ダッシュボード',
  '/reservations': '予約管理',
  '/reservations/calendar': 'カレンダー',
  '/customers': '顧客管理',
  '/customers/records': 'カルテ管理',
  '/staff': 'スタッフ管理',
  '/sales': '売上分析',
  // Issue #1913: Organization廃止 - リダイレクトされるが互換性のため残す
  '/organization/settings': '店舗情報',
  '/settings': '設定',
  '/help': 'ヘルプ',
  '/ai-analysis': 'AI分析',
  '/customers/churned': '離脱顧客',
  '/follow-up': 'フォローアップ',
  '/repeat': 'リピート作成',
  '/account': 'アカウント',
  '/onboarding': '初期設定',
  '/treatments': 'カルテ管理',
};

// パンくずリストアイテムの型定義
interface BreadcrumbItemType {
  title: string;
  href?: string;
  current?: boolean;
}

/**
 * 現在のパスからページタイトルを取得する
 * MobileHeader のタイトル表示に使用
 */
function getPageTitle(pathname: string): string {
  // 完全一致
  if (pathTitles[pathname]) return pathTitles[pathname];

  // 詳細ページパターン
  if (pathname.includes('/customers/') && pathname.split('/').length === 3) {
    return '顧客詳細';
  }
  if (pathname.includes('/reservations/') && pathname.split('/').length === 3) {
    return '予約詳細';
  }
  if (pathname.includes('/staff/') && pathname.split('/').length === 3) {
    return 'スタッフ詳細';
  }

  // セグメント末尾から推測
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0) return 'ダッシュボード';
  const last = segments[segments.length - 1];
  return pathTitles[`/${last}`] || 'ダッシュボード';
}

export function DashboardLayout() {
  const location = useLocation();
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  // 現在のパスからパンくずリストを生成 (desktop 用)
  const getBreadcrumbs = (): BreadcrumbItemType[] => {
    const path = location.pathname;

    // トップレベルページの判定（パンくず1つのみ）
    const topLevelPages = [
      '/dashboard',
      '/customers',
      '/reservations',
      '/staff',
      '/sales',
      '/settings',
      '/organization/settings',
      '/help',
      '/ai-analysis',
      '/customers/churned',
      '/repeat',
    ];

    if (topLevelPages.includes(path)) {
      const title = pathTitles[path] || 'ページ';
      return [{ title, current: true }];
    }

    // 詳細ページ（例: /customers/123）の場合
    if (
      path.includes('/customers/') &&
      path.split('/').length === 3 &&
      !path.endsWith('/records')
    ) {
      return [
        { title: 'ダッシュボード', href: '/dashboard' },
        { title: '顧客管理', href: '/customers' },
        { title: '顧客詳細', current: true },
      ];
    }

    if (
      path.includes('/reservations/') &&
      path.split('/').length === 3 &&
      !path.endsWith('/calendar')
    ) {
      return [
        { title: 'ダッシュボード', href: '/dashboard' },
        { title: '予約管理', href: '/reservations' },
        { title: '予約詳細', current: true },
      ];
    }

    if (path.includes('/staff/') && path.split('/').length === 3) {
      return [
        { title: 'ダッシュボード', href: '/dashboard' },
        { title: 'スタッフ管理', href: '/staff' },
        { title: 'スタッフ詳細', current: true },
      ];
    }

    // 通常のパス
    const segments = path.split('/').filter(Boolean);
    if (segments.length === 0) {
      return [{ title: 'ダッシュボード', current: true }];
    }

    const breadcrumbs: BreadcrumbItemType[] = [
      { title: 'ダッシュボード', href: '/dashboard' },
    ];

    let currentPath = '';
    segments.forEach((segment, index) => {
      currentPath += `/${segment}`;
      const title = pathTitles[currentPath] || segment;
      const isLast = index === segments.length - 1;

      const item: BreadcrumbItemType = {
        title,
        current: isLast,
      };

      if (!isLast) {
        item.href = currentPath;
      }

      breadcrumbs.push(item);
    });

    return breadcrumbs;
  };

  const breadcrumbs = getBreadcrumbs();
  const pageTitle = getPageTitle(location.pathname);

  return (
    <SidebarProvider defaultOpen={true}>
      {/* Desktop sidebar: md 以上でのみ表示 (AppSidebar 内部で hidden md:flex 制御) */}
      <AppSidebar />

      <SidebarInset>
        {/* ----------------------------------------------------------------
            Desktop Header (md 以上): breadcrumb 付き既存ヘッダー
            Mobile Header (md 未満): MobileHeader (Phase 1p)
            ---------------------------------------------------------------- */}

        {/* Desktop header */}
        <header className="hidden md:flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
          <div className="flex items-center gap-2 px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <Breadcrumb>
              <BreadcrumbList>
                {breadcrumbs.map((crumb, index) => (
                  <React.Fragment key={index}>
                    {index > 0 && <BreadcrumbSeparator />}
                    <BreadcrumbItem>
                      {crumb.current ? (
                        <BreadcrumbPage>{crumb.title}</BreadcrumbPage>
                      ) : crumb.href ? (
                        <BreadcrumbLink href={crumb.href}>
                          {crumb.title}
                        </BreadcrumbLink>
                      ) : (
                        <span>{crumb.title}</span>
                      )}
                    </BreadcrumbItem>
                  </React.Fragment>
                ))}
              </BreadcrumbList>
            </Breadcrumb>
          </div>
        </header>

        {/* Mobile header (md 未満) */}
        <div className="md:hidden">
          <MobileHeader
            title={pageTitle}
            onOpenDrawer={() => setMobileDrawerOpen(true)}
            drawerOpen={mobileDrawerOpen}
          />
        </div>

        {/* Main Content */}
        {/* mobile: pb-20 で BottomNav(h-16) + safe-area の高さ分 padding を確保 */}
        <div className="flex flex-1 flex-col gap-4 p-4 pt-0 pb-20 md:pb-4">
          <GlobalSearchCommand />
          <RouteErrorBoundary>
            {/* Outlet 切替時の全画面 spinner を避けるため fallback=null。
                fetch 進行表示は GlobalLoadingIndicator (top 1px bar) で担保。 */}
            <Suspense fallback={null}>
              <Outlet />
            </Suspense>
          </RouteErrorBoundary>
        </div>
      </SidebarInset>

      {/* Mobile Bottom Nav (md 未満) */}
      <div className="md:hidden">
        <BottomNav onOpenDrawer={() => setMobileDrawerOpen(true)} />
      </div>

      {/* Mobile Drawer (md 未満) */}
      <div className="md:hidden">
        <MobileDrawer
          open={mobileDrawerOpen}
          onOpenChange={setMobileDrawerOpen}
        />
      </div>

      <Toaster position="top-center" richColors />
    </SidebarProvider>
  );
}

export default DashboardLayout;
